import React, {useEffect, useRef, useState} from 'react';
import {FlatList, View} from 'react-native';
import {get, noop} from 'lodash';

import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import SortButtonWithArrow from '/component/base/SortButton/SortButtonWithArrow';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {OrderDetails, Shared} from '/constant/TestIDs';
import {PROFILE_ORDER_HISTORY} from '/enum/records.enum';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {withHOC} from '/util/hoc.util';
import {getDefaultRecordsSort, getSortOptions} from '/util/sort.util';

import {Order} from './Order';
import {styles} from './OrderHistoryList.style';

interface OrderItem {
  id: string;
  orderedOn: string;
  orderNumber: string;
  cv: string;
  qv: string;
  orderType: string;
  price: number;
  currencyCode: string;
}

interface OrderHistoryListProps {
  tt: (key: string, defaultValue: string) => string;
  style: {base: object};
  ordersIsLoading: boolean;
  orders: OrderItem[];
  fetchOnMount?: boolean;
  fetchInitialOrders?: () => void;
  safeAreaInsets?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  fetchMoreOrders: () => void;
  sortOrders: (params: any) => void;
  onOrderPress: (orderId: string) => void;
  onBackPress: () => void;
  headerTitle: string;
  featureFlag: Record<string, any>;
  meta: {
    page?: {
      total?: number | string;
    };
  };
}

export const OrderHistoryList: React.FC<OrderHistoryListProps> = ({
  tt = withTranslation.ttDefaultProp,
  style: styleProp = {base: {}},
  ordersIsLoading = false,
  orders = [],
  fetchOnMount = false,
  fetchInitialOrders = noop,
  safeAreaInsets = withSafeArea.safeAreaInsetsDefaultProp,
  fetchMoreOrders = noop,
  sortOrders = noop,
  onOrderPress = noop,
  onBackPress = noop,
  headerTitle = '',
  featureFlag = {},
  meta = {},
}) => {
  const shouldShowNewOrderHistory = shouldReleaseFeature(
    get(featureFlag, 'ff_order_history', ''),
  );
  const recordCount = Number(get(meta, 'page.total', 0)) || 0;
  const totalOrderCount = recordCount || orders.length;
  const hasOrders = orders.length > 0;
  const style = [styles.base, styleProp.base];
  const defaultSortParam = getDefaultRecordsSort({
    recordsName: PROFILE_ORDER_HISTORY,
  });
  const appliedSortParam =
    getSortOptions({
      recordsName: PROFILE_ORDER_HISTORY,
    })?.find(
      (option: any) =>
        get(option, 'params.sortBy') === get(meta, 'sortParams.sortBy') &&
        get(option, 'params.sortOrder') === get(meta, 'sortParams.sortOrder'),
    ) || defaultSortParam;
  const [sortParams, setSortParams] = useState({
    [PROFILE_ORDER_HISTORY]: appliedSortParam,
  });
  const [isSortRequestLoading, setIsSortRequestLoading] = useState(false);
  const hasTriggeredInitialFetch = useRef(false);
  const isInitialLoading = ordersIsLoading && !hasOrders;
  const isPaginationLoading =
    ordersIsLoading && hasOrders && !isSortRequestLoading;
  const headerTopInset = fetchOnMount ? safeAreaInsets?.top || 0 : 0;

  useEffect(() => {
    if (
      fetchOnMount &&
      !hasTriggeredInitialFetch.current &&
      !ordersIsLoading &&
      !hasOrders
    ) {
      hasTriggeredInitialFetch.current = true;
      fetchInitialOrders();
    }
  }, [fetchOnMount, fetchInitialOrders, hasOrders, ordersIsLoading]);

  useEffect(() => {
    setSortParams(prevState => {
      if (
        get(prevState, [PROFILE_ORDER_HISTORY, 'key']) === appliedSortParam.key
      ) {
        return prevState;
      }

      return {
        ...prevState,
        [PROFILE_ORDER_HISTORY]: appliedSortParam,
      };
    });
  }, [appliedSortParam, meta]);

  const keyExtractorRef = useRef(
    (el: any, i: any) => `${get(el, 'orderNumber')}-${i}`,
  );
  const flatListRef = useRef<FlatList<any>>(null);
  const handleSetSelectedSortParamRef = useRef(
    async ({recordsName, sortParam}: any) => {
      setSortParams(prevState => ({
        ...prevState,
        [recordsName]: sortParam,
      }));
      setIsSortRequestLoading(true);

      try {
        await sortOrders({sortParam});
      } finally {
        setIsSortRequestLoading(false);
      }
    },
  );
  const handleItemFocus = (index: number) => {
    flatListRef.current?.scrollToIndex({
      animated: true,
      index,
      viewPosition: 0.5,
    });
  };

  return (
    <View style={style}>
      <View style={[styles.header, {paddingTop: headerTopInset}]}>
        <Gutter>
          <TouchableOpacity
            testID={OrderDetails.screenBackButton}
            onPress={onBackPress}
            style={styles.backButton}
            accessibilityRole="button"
            accessibilityLabel={tt('backLabel', 'Back')}>
            <Icon name="chevron-left" style={styles.backIcon} />
            <Text style={styles.backText} isBold={true}>
              {tt('backLabel', 'Back')}
            </Text>
          </TouchableOpacity>
          <Text
            testID={Shared.headingTitle}
            variant="heading"
            isBold={true}
            style={styles.headerTitle}
            accessible={true}
            accessibilityRole="header">
            {shouldShowNewOrderHistory
              ? headerTitle || tt('header.text', 'Order History')
              : tt('header.text', 'Order History')}
          </Text>
        </Gutter>
      </View>
      {shouldShowNewOrderHistory ? (
        <Gutter>
          <View style={styles.infoRow}>
            <Text
              testID={OrderDetails.screenOrdersCount}
              style={styles.ordersCountText}
              isBold={true}
              accessible={true}
              accessibilityLabel={`${totalOrderCount} ORDERS`}>
              {tt('ordersCount', `${totalOrderCount} ORDERS`)}
            </Text>
            <SortButtonWithArrow
              recordsName={PROFILE_ORDER_HISTORY}
              sortParams={sortParams}
              setSelectedSortParam={handleSetSelectedSortParamRef.current}
              disabled={
                ordersIsLoading || isSortRequestLoading || totalOrderCount === 0
              }
            />
          </View>
        </Gutter>
      ) : null}
      <View style={styles.list}>
        {isInitialLoading || isSortRequestLoading ? (
          <LoadingIndicator
            size="large"
            style={styles.loadingIndicator}
            accessibilityLabel={tt('loadingOrders', 'Loading orders')}
          />
        ) : (
          <FlatList
            testID={OrderDetails.screenList}
            ref={flatListRef}
            initialNumToRender={10}
            viewabilityConfig={{
              viewAreaCoveragePercentThreshold: 95,
            }}
            data={orders}
            keyExtractor={keyExtractorRef.current}
            renderItem={data => (
              <Order
                onOrderPress={onOrderPress}
                onFocus={() => handleItemFocus(data.index)}
                totalCount={totalOrderCount}
                {...data}
              />
            )}
            onEndReached={fetchMoreOrders}
            onEndReachedThreshold={0.5}
            accessibilityLabel={tt('orderHistoryList', 'Order history list')}
            ListFooterComponent={
              isPaginationLoading ? (
                <LoadingIndicator
                  size="small"
                  style={styles.listFooterLoadingIndicator}
                  accessibilityLabel={tt(
                    'loadingMoreOrders',
                    'Loading more orders',
                  )}
                />
              ) : null
            }
          />
        )}
      </View>
    </View>
  );
};

OrderHistoryList.defaultProps = {
  fetchOnMount: false,
  fetchInitialOrders: noop,
  safeAreaInsets: withSafeArea.safeAreaInsetsDefaultProp,
};

export default withHOC(
  withSafeArea,
  withTranslation('screen.orderHistoryList'),
)(OrderHistoryList);
